/**
 * letterheadHtml.js — builds a self-contained, printable STR letterhead with the
 * admin's own text placed onto it. Two colourways (dark obsidian / light bone),
 * matching the brand letterhead: STR | STRATEGISTS lockup, contact block, one
 * brass hairline, Inter Tight body, serif signature + mono role, and a mono
 * footer strip. Opens in a new tab with a Download PDF button (window.print).
 */

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Turn plain text into paragraphs: blank line → new <p>, single newline → <br>.
function bodyToHtml(text) {
  const blocks = String(text || '').replace(/\r\n/g, '\n').split(/\n{2,}/);
  return blocks
    .map(b => b.trim())
    .filter(Boolean)
    .map(b => `<p>${esc(b).replace(/\n/g, '<br/>')}</p>`)
    .join('');
}

const CONTACT = {
  site: 'STRATEGISTSPK.COM',
  phone: '+92 322 0000366',
  city: 'FAISALABAD, PK',
};

export function buildLetterheadHtml({
  body = '',
  date = '',
  greeting = 'Dear —',
  signName = 'Shehryar',
  signRole = 'Founder',
  colorway = 'dark',
} = {}) {
  const light = colorway === 'light';
  const C = light
    ? { bg: '#EDE9E0', text: '#1A1815', mut: '#6B655C', faint: '#8F887C', gold: '#A98953', line: 'rgba(26,24,21,0.16)' }
    : { bg: '#0A0A0C', text: '#EFEBE4', mut: 'rgba(239,235,228,0.55)', faint: 'rgba(239,235,228,0.42)', gold: '#A98953', line: 'rgba(239,235,228,0.14)' };

  return `<!doctype html><html lang="en"><head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>STR · Letterhead</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400&family=Bodoni+Moda:opsz,wght@6..96,400;6..96,500&family=Inter+Tight:wght@300;400;500&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box} html,body{margin:0}
  body{background:${C.bg};color:${C.text};font-family:'Inter Tight',system-ui,sans-serif;font-weight:300;-webkit-font-smoothing:antialiased}
  .serif{font-family:'Playfair Display',Georgia,serif;font-weight:400}
  .mono{font-family:'JetBrains Mono',ui-monospace,monospace}
  .sheet{max-width:860px;margin:0 auto;min-height:100vh;padding:76px 74px 56px;display:flex;flex-direction:column}
  /* Header */
  .head{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;padding-bottom:28px;border-bottom:1px solid ${C.gold}}
  .lockup{display:flex;align-items:center;gap:18px}
  .mark{font-family:'Bodoni Moda','Didot',Georgia,serif;font-weight:400;font-size:40px;letter-spacing:.24em;text-indent:.24em;color:${C.text};line-height:1}
  .rule{width:1px;align-self:stretch;background:${C.gold};opacity:.6}
  .desc{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:${C.mut};line-height:1.7;max-width:80px}
  .contact{text-align:right;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:9.5px;letter-spacing:.16em;text-transform:uppercase;color:${C.mut};line-height:2}
  /* Body */
  .date{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:${C.faint};margin:54px 0 34px}
  .letter{max-width:640px;font-size:15px;font-weight:300;line-height:1.75;color:${C.text}}
  .letter .greet{margin:0 0 22px}
  .letter p{margin:0 0 20px}
  .sign{margin-top:44px}
  .sign .name{font-family:'Playfair Display',Georgia,serif;font-weight:400;font-size:30px;color:${C.text};line-height:1.1}
  .sign .role{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:9.5px;letter-spacing:.2em;text-transform:uppercase;color:${C.gold};margin-top:8px}
  /* Footer */
  .foot{margin-top:auto;padding-top:22px;border-top:1px solid ${C.line};display:flex;justify-content:space-between;gap:20px}
  .foot .mono{font-size:9px;letter-spacing:.18em;text-transform:uppercase;color:${C.faint}}
  #pdfbtn{position:fixed;bottom:20px;right:20px;z-index:40;background:${C.gold};color:#0A0A0C;border:1px solid ${C.gold};border-radius:2px;padding:12px 20px;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:10px;font-weight:400;letter-spacing:.14em;text-transform:uppercase;cursor:pointer;box-shadow:0 10px 30px rgba(0,0,0,.4)}
  @media print{
    html,body{margin:0;background:${C.bg};-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .sheet{max-width:none;width:100%;min-height:100vh}
    #pdfbtn{display:none!important}
    @page{margin:0}
  }
</style></head>
<body>
  <div class="sheet">
    <div class="head">
      <div class="lockup">
        <div class="mark">STR</div>
        <div class="rule"></div>
        <div class="desc">Strate&shy;gists</div>
      </div>
      <div class="contact">${esc(CONTACT.site)}<br/>${esc(CONTACT.phone)}<br/>${esc(CONTACT.city)}</div>
    </div>

    <div class="date">${esc(date)}</div>
    <div class="letter">
      <p class="greet">${esc(greeting)}</p>
      ${bodyToHtml(body) || '<p style="opacity:.4">Your letter text appears here…</p>'}
    </div>
    <div class="sign">
      <div class="name serif">${esc(signName)}</div>
      <div class="role">${esc(signRole)}</div>
    </div>

    <div class="foot">
      <span class="mono">Strategists · Performance · Content · Web</span>
      <span class="mono">Page 1</span>
    </div>
  </div>

  <button id="pdfbtn" onclick="downloadPdf()">↓ Download PDF</button>
  <script>
  function downloadPdf(){
    var sheet=document.querySelector('.sheet');
    var W=860, prevW=sheet.style.width, prevMax=sheet.style.maxWidth;
    sheet.style.maxWidth='none'; sheet.style.width=W+'px';
    function go(){
      var H=Math.max(1180, Math.ceil(sheet.getBoundingClientRect().height));
      var st=document.getElementById('pageStyle')||document.createElement('style');
      st.id='pageStyle'; st.textContent='@page{size:'+W+'px '+H+'px;margin:0}';
      document.head.appendChild(st);
      setTimeout(function(){ window.print(); sheet.style.width=prevW; sheet.style.maxWidth=prevMax; }, 60);
    }
    if(document.fonts && document.fonts.ready){ document.fonts.ready.then(function(){ setTimeout(go,40); }); } else { go(); }
  }
  if(location.search.indexOf('print')>=0){ window.addEventListener('load',function(){ setTimeout(downloadPdf,700); }); }
  </script>
</body></html>`;
}

// Open the letterhead in a new tab, ready to view / save as PDF.
export function openLetterheadDocument(opts) {
  const html = buildLetterheadHtml(opts || {});
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
